import { useTheme } from "@emotion/react";
import { Button } from "@mui/material";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { AudioSchedulerContext } from "../audio/audioSchedulerContext";
import { useMuted } from "../audio/mutedContext";
import MuteButton from "../components/MuteButton";
import { PhaseRingSegments } from "../components/PhaseRingSegments";
import { useConfig } from "../config/configContext";
import { Phase, Room, Session } from "../config/parsedConfig";
import { useCurrentSession } from "../hooks/useCurrentSession";
import { useUpdateTitle } from "../hooks/useUpdateTitle";
import { getThemeColor } from "../utils/color";
import { randomElement, randomInt } from "../utils/random";
import { convertSecondsToDurationString, convertSecondsToDurationString_Text, convertSecondsToLocal_HH_MM, convertSecondsToTimestamp } from "../utils/time";
import NotFound from "./404";

export interface PlannedPhase extends Phase {
    /**
     * The number of seconds between the end of this phase and the end of the entire session.
     * The last phase of the session has ``displayPhaseEndToSessionEnd = 0``.
     * This number is used to determine the angle-position of the RingElement that displays the phase.
     */
    displayPhaseEndToSessionEnd?: number;
    /**
     * The number of seconds between the start of this phase and the end of the entire session.
     * This number is used to determine the angle-position of the RingElement that displays the phase.
     * For phases with ``hiddenOnClock = true``, this number equals ``displayPhaseEndToSessionEnd``.
     */
    displayPhaseStartToSessionEnd?: number;
    /** seconds that this phase has been running already */
    runningTime?: number;
}

/**
 * Calculates the upcoming phases of a session. 
 * @param param0 
 * @returns An array of phases that are upcoming in the session. The first phase is the current phase. 
 * It's duration is shortened by the time that has already passed in the current phase.
 */
export const getUpcomingPhases = (phases: Phase[], currentSessionRunningTime: number, keepLastPhase?: boolean) => {
    const upcomingPhases: PlannedPhase[] = [];
    //iterate over phases
    //use ``currentSessionRunningTime`` to skip over all phases that are already over and to cut off the already-passed part of the current phase
    //add those (modified) phases to ``statusPhases``
    phases.forEach(phase => {
        if (currentSessionRunningTime <= 0) {
            //phase is in the future
            upcomingPhases.push(phase);
        } else if (phase.duration > currentSessionRunningTime) {
            //current phase
            upcomingPhases.push({
                ...phase,
                runningTime: currentSessionRunningTime,
                duration: phase.duration - currentSessionRunningTime
            });
            currentSessionRunningTime = 0;
        } else {
            //phase is in the past. don't add it to upcomingPhases. 
            //subtract its duration from currentSessionRunningTime for this forEach-loop
            currentSessionRunningTime -= phase.duration;
        }
    });
    if (keepLastPhase && upcomingPhases.length === 0 && phases.length > 0) {
        //add last phase with duration 0
        upcomingPhases.push({
            ...phases.at(-1)!,
            duration: 0
        });
    }
    //calculate the phaseEndToSessionEnd for each phase
    //iterate upcomingPhases backwards and add the duration of each phase to the phaseEndToSessionEnd of the next phase
    let toSessionEnd = 0;
    for (let i = upcomingPhases.length - 1; i >= 0; i--) {
        upcomingPhases[i].displayPhaseEndToSessionEnd = toSessionEnd;
        if (!upcomingPhases[i].hiddenOnClock) {
            toSessionEnd += upcomingPhases[i].duration;
        }
        upcomingPhases[i].displayPhaseStartToSessionEnd = toSessionEnd;
    }
    // console.debug("upcomingPhases", {phases, currentSessionRunningTime, upcomingPhases});
    return upcomingPhases;
}


export const DEFAULT_PAUSE_IMAGES = [
    "/img/Schreibtisch.png",
];

export const DEFAULT_DOOR_IMAGE = "/img/door-green.png";
const DISPLAY_DURATION_TILL_UPCOMING_SESSION = 5 * 60; //5 minutes

export const UpcomingSessionInfo = ({ upcomingSession, upcomingSessionIsTomorrow, roomColor, timeUntilUpcomingSession }: { upcomingSession: Session, upcomingSessionIsTomorrow: boolean, timeUntilUpcomingSession: number, roomColor: string }) => {
    const config = useConfig();

    const upcomingSessionLocalTime = useMemo(() => {
        return convertSecondsToLocal_HH_MM(upcomingSession.startTime, upcomingSessionIsTomorrow, config.timezone);
    }, [upcomingSession, upcomingSessionIsTomorrow, config.timezone]);

    return (<p>
        Gerade ist Pause!<br />
        <span className="text-bold" style={{ color: roomColor }}>{upcomingSession.name}</span><br />
        {timeUntilUpcomingSession < DISPLAY_DURATION_TILL_UPCOMING_SESSION
            ? <span>
                startet in <span className="text-bold" >{convertSecondsToDurationString_Text(timeUntilUpcomingSession)}</span>.
            </span>
            : <span>
                startet {upcomingSessionLocalTime.locallyTomorrow && <span className="text-bold" >morgen</span>} um <span className="text-bold" >{upcomingSessionLocalTime.localTime} Uhr</span>.
            </span>
        }
    </p>);
};

export const RoomDisplay = ({ room }: { room: Room }) => {
    const {
        currentSession,
        upcomingSession,
        upcomingSessionIsTomorrow,
        currentSessionRunningTime,
        timeUntilUpcomingSession,
        currentDateInTimezone,
    } = useCurrentSession(room);

    const [audioScheduler, playingAudio] = useContext(AudioSchedulerContext);

    // const { getNowInMillis } = useContext(NowContext);

    const theme = useTheme() as any;
    const roomColor = getThemeColor(theme, room.color);
    const config = useConfig();

    const [inside, setInside] = useState(false);
    const isPlayingAudio = playingAudio !== null;

    const [muted, setMuted] = useMuted();

    const joinZoom = useCallback(() => {
        if (room.zoomLink) {
            //join zoom: open link from room in new tab
            window.open(room.zoomLink, "_blank");
        }
    }, [room.zoomLink]);

    const leaveRoom = useCallback(() => {
        //stop audio and set inside to false
        audioScheduler.stopAllIncludingSilence();
        setInside(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const enterRoom = useCallback(() => {
        if (!inside) {
            setInside(true);
            setMuted(false);
            audioScheduler.playSilence();

            if (room.zoomLink) {
                //auto join zoom?
                const autoJoinZoom = currentSession
                    ? (!!config.zoom.autoJoin_afterSessionStart && currentSessionRunningTime <= config.zoom.autoJoin_afterSessionStart)
                    : (!!config.zoom.autoJoin_beforeSessionStart && timeUntilUpcomingSession <= config.zoom.autoJoin_beforeSessionStart);
                if (autoJoinZoom) {
                    joinZoom();
                }
            }
        }
    }, [inside, setMuted, audioScheduler, room.zoomLink, currentSession, config.zoom.autoJoin_afterSessionStart, config.zoom.autoJoin_beforeSessionStart, currentSessionRunningTime, timeUntilUpcomingSession, joinZoom]);

    const theDiceAreThrown = useMemo(() => {
        let currentSeed = currentDateInTimezone + "_" + room.name + (currentSession ? "_" + currentSession.name + "_" + currentSession.startTime : "");
        let upcomingSeed = currentDateInTimezone + "_" + room.name + "_" + upcomingSession.name + "_" + upcomingSession.startTime;
        const currentAudioFlaverIndex = randomInt(currentSession?.audioFlavors.length || 0, currentSeed);
        const upcomingAudioFlaverIndex = randomInt(upcomingSession.audioFlavors.length || 0, upcomingSeed);
        const pauseImage = randomElement(room.pauseImages || DEFAULT_PAUSE_IMAGES, upcomingSeed);
        console.debug("theDiceAreThrown 🎲", { currentSeed, upcomingSeed, currentAudioFlaverIndex, upcomingAudioFlaverIndex, pauseImage })
        return {
            currentAudioFlaverIndex,
            upcomingAudioFlaverIndex,
            pauseImage
        };
    }, [currentDateInTimezone, room, currentSession, upcomingSession]);

    const allAudios = useMemo(() => {
        return [
            ...(currentSession ? currentSession.phases.map(phase => phase.audio?.[theDiceAreThrown.currentAudioFlaverIndex]) : []),
            ...(upcomingSession !== currentSession ? upcomingSession.phases.map(phase => phase.audio?.[theDiceAreThrown.upcomingAudioFlaverIndex]) : [])
        ].filter(audio => audio) as string[];
    }, [currentSession, upcomingSession, theDiceAreThrown]);

    //preload all audios
    useEffect(() => {
        audioScheduler.preload(allAudios);
    }, [audioScheduler, allAudios]);

    const backgroundImage = inside
        ? currentSession
            ? currentSession.graphic.url
            : //random pause image 
            theDiceAreThrown.pauseImage
        : (room.doorImage || DEFAULT_DOOR_IMAGE);
    const backgroundImageHover = inside ? undefined : room.doorImageHover;
    const exitDorImage = room.doorImageHover || room.doorImage || DEFAULT_DOOR_IMAGE;

    const upcomingPhases = useMemo(() => {
        if (!currentSession) {
            return [];
        }
        return getUpcomingPhases(currentSession.phases, currentSessionRunningTime);
    }, [currentSession, currentSessionRunningTime]);

    const currentPhase = upcomingPhases[0];

   
    /**
     * The audio that is currently playing or will be played next, together with the startTime of its phase.
     * Null if no audio is scheduled.
     */
    const currentOrNextAudio = useMemo(() => {
        if (!inside) return null;
        //check if there is an audio that is currently playing
        //iterate over upcoming phases and return the first one that has audio
        const upcomingPhaseWithAudio = upcomingPhases.find(phase => phase.audio);
        if (upcomingPhaseWithAudio) {
            return {
                url: upcomingPhaseWithAudio.audio![theDiceAreThrown.currentAudioFlaverIndex],
                timestamp: convertSecondsToTimestamp(upcomingPhaseWithAudio.startTime, config.timezone, upcomingPhaseWithAudio !== upcomingPhases[0])
            };
        }
        //no audio found in the current session. check upcoming session
        const upcomingSessionPhaseWithAudio = upcomingSession.phases.find(phase => phase.audio);
        if (upcomingSessionPhaseWithAudio) {
            return {
                url: upcomingSessionPhaseWithAudio.audio![theDiceAreThrown.upcomingAudioFlaverIndex],
                timestamp: convertSecondsToTimestamp(upcomingSessionPhaseWithAudio.startTime, config.timezone, true)
            };
        }
        //no audio found in the upcoming session. return null
        return null;
    }, [inside, upcomingPhases, upcomingSession, theDiceAreThrown, config.timezone]);

    //play (or schedule) the audio
    useEffect(() => {
        // console.debug("currentAudio changed", { url: currentOrNextAudio?.url, timestamp: currentOrNextAudio?.timestamp });
        if (currentOrNextAudio?.url) {
            //play audio
            audioScheduler.playAt(currentOrNextAudio.url, currentOrNextAudio?.timestamp);
        }
        // return () => {
        //     // console.debug("currentAudio cleanup stop", { url: currentOrNextAudio?.url, timestamp: currentOrNextAudio?.timestamp });
        //     audioScheduler.stop();
        // }
    }, [audioScheduler, currentOrNextAudio?.url, currentOrNextAudio?.timestamp]);


    useUpdateTitle({ currentPhase, inside, timeUntilUpcomingSession, roomName: room.displayName });

    return (
        <div
            className={"responsive room " +
                (inside ? "inside" : "outside") +
                (isPlayingAudio && inside ? " is-playing-audio" : "") +
                (currentSession ? " session " : " pause ")
            }
            id={"room-" + room.name}
        >

            <div className="room-head">
                {inside ?
                    <Button
                        key="leave-room"
                        variant='outlined'
                        color={room.color as any}
                        startIcon={<div style={{ backgroundImage: `url(${exitDorImage})` }} />}
                        size='large'
                        fullWidth={true}
                        onClick={leaveRoom}
                        sx={{
                        }}
                    >
                        ◀ &nbsp; Verlassen
                    </Button> 
                    :
                    <Button
                        key="enter-room"
                        variant='contained'
                        color={room.color as any}
                        size='large'
                        fullWidth={true}
                        onClick={enterRoom}
                        sx={{
                        }}
                    >
                        Beitreten &nbsp; ▶
                    </Button>
                }
            </div>

            <div className="room-image-container">
                <div className="layer img door"
                    style={{ backgroundImage: `url(${backgroundImage})`, cursor: inside ? "default" : "pointer" }}
                    title={inside ? undefined : "Beitreten   ▶"}
                    onClick={enterRoom}
                >
                </div>
                {backgroundImageHover &&
                    <div className="layer img door-open"
                        style={{ backgroundImage: `url(${backgroundImageHover})`, cursor: "pointer" }}
                        title={inside ? undefined : "Beitreten   ▶"}
                        onClick={enterRoom}
                    >
                    </div>
                }
                {inside && isPlayingAudio && currentSession?.graphic.urlWithVolume &&
                    <div className="layer img only-show-if-audio-is-playing"
                        style={{ backgroundImage: `url(${currentSession.graphic.urlWithVolume})` }}
                    >
                    </div>
                }
                {inside && currentSession &&
                    <PhaseRingSegments passedTime={currentSessionRunningTime} upcomingPhases={upcomingPhases} graphic={currentSession.graphic}
                    />
                }
            </div>

            <div className="room-info">
                {inside
                    ? //inside 
                    <>
                        {currentSession
                            ? // display current session name, current phase, time left of current phase (MM:SS)
                            <div className="text-center">
                                <p>
                                    <span style={{ color: roomColor }}>{currentSession.name}</span>
                                </p>
                                {currentPhase &&
                                    <>
                                        <p>
                                            <span className="text-bold" style={{ color: roomColor }}>{currentPhase.name}</span>
                                            <span style={{ position: "relative", display: "inline-block", top: -1 }}>
                                                <MuteButton isPlayingAudio={!!(inside && isPlayingAudio)} color={room.color} />
                                            </span>
                                            <br />
                                            noch <span className="text-bold">{convertSecondsToDurationString(currentPhase.duration)}</span>
                                        </p>
                                        {
                                            currentPhase.description &&
                                            <p>
                                                {currentPhase.description}
                                            </p>
                                        }
                                    </>
                                }
                            </div>
                            : // display upcoming session: name, start time of day (local time! local time format!)
                            <div className="text-center">
                                <UpcomingSessionInfo
                                    timeUntilUpcomingSession={timeUntilUpcomingSession}
                                    upcomingSession={upcomingSession}
                                    upcomingSessionIsTomorrow={upcomingSessionIsTomorrow}
                                    roomColor={roomColor} />

                                <p className="with-border" style={{ borderColor: roomColor }}>
                                    {/* 🔊 Bitte lasse die Lautstärke<br />Deines PCs hochgedreht:<br /> */}
                                    <span style={{ color: roomColor }}>
                                        Der <b>Gong</b> <MuteButton isPlayingAudio={false} color={room.color} /><br />
                                        {muted
                                            ? <>
                                                wird Dich <b>nicht</b> zur Session rufen,<br />
                                                da Du den Ton stummgeschaltet hast!
                                            </>
                                            : <>
                                                wird Dich zur Session rufen.<br />
                                                Bitte lass dieses Fenster dafür offen und sichtbar.
                                            </>
                                        }
                                    </span>
                                </p>
                            </div>
                        }
                        {room.zoomLink &&
                            (currentSession ||
                                (!!config.zoom.buttonVisible_beforeSessionStart && config.zoom.buttonVisible_beforeSessionStart >= timeUntilUpcomingSession))
                            && //maybe display zoom link 
                            <div className="room-info-button">
                                <Button
                                    endIcon={<div style={{ backgroundImage: "url(/img/zoom-logo.png)" }} />}
                                    variant='contained'
                                    color={room.color as any}
                                    size='large'
                                    fullWidth={true}
                                    onClick={joinZoom}
                                    sx={{
                                    }}
                                >
                                    In Zoom beitreten
                                </Button>
                            </div>
                        }
                    </>
                    : //outside 
                    <>
                        {currentSession
                            ? // display current session name, and time that is has been running for
                            <div className="text-center">
                                <p>
                                    <span className="text-bold" style={{ color: roomColor }}>{currentSession.name}</span><br />
                                    läuft seit <span className="text-bold">{convertSecondsToDurationString(currentSessionRunningTime)}</span>.
                                </p>
                            </div>
                            : // display upcoming session: name, start time of day (local time! local time format!)
                            <div className="text-center">
                                <UpcomingSessionInfo
                                    timeUntilUpcomingSession={timeUntilUpcomingSession}
                                    upcomingSession={upcomingSession}
                                    upcomingSessionIsTomorrow={upcomingSessionIsTomorrow}
                                    roomColor={roomColor} />
                            </div>
                        }
                    </>
                }

            </div>
        </div>
    );
};

export const RoomRoute = () => {
    const { roomName } = useParams();
    const config = useConfig();
    const room = roomName && config.rooms[roomName];
    if (!room) {
        return (
            <NotFound />
        );
    }
    return <RoomDisplay room={room} />;
};
